import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, Tag, Button, Collapse, Empty, Spin, Popconfirm, message } from 'antd'
import { EditOutlined, PlusOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import { SelectC, InputC } from '@/i2antdV5'
import QueryManager from '@/utils/queryManager'
import ApiPollingManager from '@/utils/ApiPollingManager'
import {
  fetchNotificationTemplates,
  fetchNotificationRules,
  deleteNotificationRuleAction
} from '@/redux/order/serviceNotificationSlice'
import { fetchEnumOptions } from '@/redux/components/enumOptionsSlice'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { fetchChuanglanTemplateOptions } from '@/redux/components/chuanglanTemplateOptionsSlice'

import ServiceNotificationModal from './share/ServiceNotificationModal'
import NotificationRuleModal from './share/NotificationRuleModal'
import NotificationRuleCopyModal from './share/NotificationRuleCopyModal'
import { renderTextStr, formatDateTimeStr } from '@/utils/renderUtils'
import './serviceNotificationList.less'

const { Panel } = Collapse

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '订单管理', href: '/client/order/orderList' },
  { breadcrumbName: '服务通知管理' }
]

// 获取默认查询参数
const getDefaultQuery = () => {
  return {
    page: 1,
    pageSize: 10
  }
}

/** 登录用户所属门店（接口 storeId；兼容 store_id） */
function getLoginUserStoreId (user) {
  if (!user || typeof user !== 'object') return ''
  const raw = user.storeId != null && user.storeId !== '' ? user.storeId : user.store_id
  if (raw == null || raw === '') return ''
  return String(raw)
}

const defaultQuery = getDefaultQuery()

/** enumOptions `option` 分桶：id 为枚举 value，value 为 desc */
function enumOptionLabel (options, rawId) {
  if (rawId === undefined || rawId === null || rawId === '') return ''
  if (!Array.isArray(options) || options.length === 0) return ''
  const idStr = `${rawId}`
  const hit = options.find((o) => `${o.id}` === idStr)
  return hit ? hit.value : ''
}

/** 规则所属门店 id（兼容 store_id） */
function ruleSourceStoreId (rule) {
  if (!rule || typeof rule !== 'object') return ''
  const raw =
    rule.storeId != null && rule.storeId !== ''
      ? rule.storeId
      : rule.store_id
  if (raw == null || raw === '') return ''
  return String(raw)
}

/** 拷贝目标门店选项：排除规则当前所属门店 */
function copyTargetStoreOptions (rule, storeOptions) {
  if (!Array.isArray(storeOptions) || storeOptions.length === 0) return []
  const sourceId = ruleSourceStoreId(rule)
  if (!sourceId) return storeOptions
  return storeOptions.filter((o) => String(o.id) !== sourceId)
}

/** 规则卡片：门店展示名（优先接口 storeName，否则 JAVA 门店选项） */
function ruleStoreDisplayName (rule, storeOptions) {
  if (!rule || typeof rule !== 'object') return '—'
  const named = rule.storeName || rule.store_name
  if (named != null && String(named).trim() !== '') return String(named).trim()
  const fromOpts = enumOptionLabel(storeOptions, rule.storeId ?? rule.store_id)
  if (fromOpts) return fromOpts
  if (rule.storeId != null && rule.storeId !== '') return String(rule.storeId)
  if (rule.store_id != null && rule.store_id !== '') return String(rule.store_id)
  return '—'
}

/**
 * 接口配置管理
 */
const getAPIConfig = (componentInstance) => ({
  // 只需加载一次的接口配置
  once: [],
  // 实时获取接口配置
  realtime: [],
  // 定时获取接口配置
  polling: [],
  // 手动触发接口
  manual: [
    {
      name: 'fetchNotificationTemplates',
      dispatch: fetchNotificationTemplates,
      getParams: (query) => ({ query: { ...query } })
    }
  ]
})

class ServiceNotificationListPage extends React.Component {
  constructor(props) {
    super(props)
    this.lastUpdateTime = 0
    this.lastUpdateKey = null

    // 初始化QueryManager
    this.queryManager = new QueryManager(defaultQuery, {
      syncToUrl: true
    })
    
    // 创建 API 轮询管理器
    this.apiManager = new ApiPollingManager(getAPIConfig(this), this)
    
    // 设置更新回调
    this.queryManager.setUpdateCallback((query) => {
      this.apiManager.fetchData(query, true)
    })
    
    // 设置navigate函数
    if (props.navigate) {
      this.queryManager.setNavigate(props.navigate)
    }
  }

  componentDidMount() {
    const { location } = this.props
    const initializedQuery = this.queryManager.initialize(location)
    
    // 重写 fetchData 方法
    const originalFetchData = this.apiManager.fetchData.bind(this.apiManager)
    this.apiManager.fetchData = async (value, showErrorDialog = true) => {
      if (value) {
        const oldCallback = this.queryManager.onUpdateCallback
        this.queryManager.setUpdateCallback(null)
        this.queryManager.updateQuery(value, undefined, { 
          autoFetch: false,
          syncToUrl: this.queryManager.syncToUrl
        })
        this.queryManager.setUpdateCallback(oldCallback)
      }
      const currentQuery = this.queryManager.getQuery()
      this.apiManager.currentQuery = currentQuery
      return originalFetchData(currentQuery, showErrorDialog)
    }
    
    // 初始化管理器
    this.apiManager.currentQuery = initializedQuery
    this.apiManager.init(initializedQuery)

    this.props.dispatch(
      fetchEnumOptions({
        key: 'NotifyCategory',
        dataType: 'notifyCategory'
      })
    )
    this.props.dispatch(
      fetchEnumOptions({
        key: 'NotificationChannel',
        dataType: 'option'
      })
    )
    this.props.dispatch(
      fetchEnumOptions({
        key: 'NotificationTargetType',
        dataType: 'option'
      })
    )
    this.props.dispatch(
      fetchEnumOptions({
        key: 'NotificationOffsetUnit',
        dataType: 'option'
      })
    )
    this.props.dispatch(
      fetchEnumOptions({
        key: 'NotificationLifecycle',
        dataType: 'option'
      })
    )
    this.props.dispatch(fetchNotificationRules({ query: {} }))
    this.props.dispatch(fetchJStoreList({ query: {}, dataType: 'optionErp' }))
    this.props.dispatch(fetchChuanglanTemplateOptions({ query: {}, dataType: 'option' }))
  }

  componentDidUpdate(prevProps) {
    this.apiManager.checkReduxErrors(prevProps, () => ({
      fetchNotificationTemplates: this.props.serviceNotificationObj?.list
    }))
  }

  componentWillUnmount() {
    this.apiManager.cleanup()
  }

  /**
   * 更新query参数
   */
  updateQuery = (keyOrUpdates, value, options = {}) => {
    const isActionCall = typeof keyOrUpdates === 'object' && keyOrUpdates !== null && options.syncToUrl === undefined
    
    const now = Date.now()
    let updateKey = null
    
    if (typeof keyOrUpdates === 'string') {
      updateKey = `${keyOrUpdates}:${JSON.stringify(value)}`
    } else if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      const keys = Object.keys(keyOrUpdates).sort()
      updateKey = keys.map(k => `${k}:${JSON.stringify(keyOrUpdates[k])}`).join('|')
    }
    
    if (updateKey && 
        this.lastUpdateKey === updateKey && 
        now - this.lastUpdateTime < 300) {
      return this.queryManager.getQuery()
    }
    
    this.lastUpdateTime = now
    this.lastUpdateKey = updateKey
    
    if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      const currentQuery = this.queryManager.getQuery()
      let hasChange = false
      for (const key in keyOrUpdates) {
        if (currentQuery[key] !== keyOrUpdates[key]) {
          hasChange = true
          break
        }
      }
      if (!hasChange) {
        return currentQuery
      }
    } else if (typeof keyOrUpdates === 'string') {
      const currentQuery = this.queryManager.getQuery()
      if (currentQuery[keyOrUpdates] === value) {
        return currentQuery
      }
    }
    
    const finalOptions = {
      autoFetch: true,
      syncToUrl: options.syncToUrl !== undefined 
        ? options.syncToUrl 
        : (isActionCall ? false : this.queryManager.syncToUrl),
      ...options
    }
    return this.queryManager.updateQuery(keyOrUpdates, value, finalOptions)
  }

  /**
   * 获取当前query参数
   */
  getQuery = () => {
    return this.queryManager.getQuery()
  }

  /**
   * 获取数据
   */
  fetchData = async (value, showErrorDialog = true) => {
    return this.apiManager.fetchData(value, showErrorDialog)
  }

  /**
   * 保存成功回调
   */
  handleSaveSuccess = () => {
    const currentQuery = this.getQuery()
    this.fetchData(currentQuery)
    this.props.dispatch(fetchNotificationRules({ query: {} }))
  }

  handleRuleCreatedSuccess = () => {
    this.props.dispatch(fetchNotificationRules({ query: {} }))
  }

  handleDeleteNotificationRule = (ruleId) => {
    return this.props.dispatch(deleteNotificationRuleAction({ id: ruleId }))
      .unwrap()
      .then(() => {
        message.success('删除成功')
        this.handleRuleCreatedSuccess()
      })
      .catch((payload) => {
        const errMsg =
          typeof payload === 'string'
            ? payload
            : payload?.msg || payload?.message || '删除失败'
        message.error(errMsg)
      })
  }

  /** 优先使用后端 channelsDesc */
  notificationRuleChannelLabel = (rule) => {
    return rule.channelsDesc || rule.channelsDescription || '其他渠道'
  }

  /** 规则触发间隔：offsetValue + offsetUnitDesc（兼容下划线字段；单位可兜底 NotificationOffsetUnit） */
  formatRuleTriggerInterval = (rule) => {
    const { notificationOffsetUnitEnum: { data: offsetUnitOptions = [] } = {} } = this.props
    const rawVal =
      rule.offsetValue !== undefined && rule.offsetValue !== null
        ? rule.offsetValue
        : rule.offset_value
    const rawUnitId =
      rule.offsetUnit !== undefined && rule.offsetUnit !== null && rule.offsetUnit !== ''
        ? rule.offsetUnit
        : rule.offset_unit
    const unit =
      rule.offsetUnitDesc ||
      rule.offset_unit_desc ||
      enumOptionLabel(offsetUnitOptions, rawUnitId) ||
      ''
    const hasNum = rawVal !== undefined && rawVal !== null && rawVal !== ''
    if (!hasNum && !unit) return '—'
    const numPart = hasNum ? String(rawVal) : ''
    const merged = [numPart, unit].filter(Boolean).join(unit && numPart ? ' ' : '')
    return merged || '—'
  }

  groupNotificationRulesByChannel = (rules) => {
    const grouped = {}
    rules.forEach((r) => {
      const lb = this.notificationRuleChannelLabel(r)
      if (!grouped[lb]) grouped[lb] = []
      grouped[lb].push(r)
    })
    return grouped
  }

  renderNotificationRulesInScenePanel = (rules, scenePanel) => {
    console.log('renderNotificationRulesInScenePanel',rules, scenePanel)
    if (!rules || rules.length === 0) {
      return (
        <Empty
          className="notification-scene-panel-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无通知规则"
          style={{ padding: '28px 20px 32px' }}
        />
      )
    }
    const panelSceneId =
      scenePanel != null && scenePanel.id !== undefined && scenePanel.id !== null
        ? String(scenePanel.id)
        : ''
    const panelSceneLabel =
      scenePanel != null && scenePanel.value != null ? String(scenePanel.value) : ''
    const {
      notificationTargetTypeEnum: { data: targetTypeOptions = [] } = {},
      serviceNotificationObj: {
        deleteNotificationRule: { fetching: ruleDeleteFetching },
        copyNotificationRule: { fetching: ruleCopyFetching }
      }
    } = this.props
    const channelFilterOptions =
      (this.props.notificationChannelEnum && this.props.notificationChannelEnum.data) || []
    const storeOptions = (this.props.jStoreListObj && this.props.jStoreListObj.data) || []
    const chuanglanTemplateOptions =
      (this.props.chuanglanTemplateOptionsObj && this.props.chuanglanTemplateOptionsObj.data) || []
    const userDefaultStore = getLoginUserStoreId(this.props.user)
    const byChannel = this.groupNotificationRulesByChannel(rules)
    return Object.entries(byChannel).map(([channelLabel, items]) => (
      <div key={channelLabel} className="notification-rule-channel-section">
        <div className="notification-rule-channel-heading">
          <span className="notification-rule-channel-heading__accent" aria-hidden />
          <div className="notification-rule-channel-heading__text">
            <span className="notification-rule-channel-heading__label">渠道</span>
            <Tag className="notification-rule-channel-heading__tag" color="processing">
              {channelLabel}
            </Tag>
            <span className="notification-rule-channel-count">{items.length} 条规则</span>
          </div>
        </div>
        <div className="notification-rule-cards-wrap">
          <div className="template-cards-grid notification-rule-cards-grid">
          {items.map((rule) => (
            <div key={String(rule.id)} className="template-card notification-rule-card">
              <div className="template-card-header notification-rule-card__header">
                <div className="template-card-title">
                  <h4>{renderTextStr(rule.name)}</h4>
                  <Tag color={rule.enabled ? 'success' : 'default'}>
                    {rule.enabled ? '启用' : '停用'}
                  </Tag>
                </div>
                <div className="notification-rule-card__actions">
                  <NotificationRuleModal
                    baseProps={{
                      actType: 'edit',
                      editingRule: rule,
                      defaultScene:
                        rule.notifyCategory != null && rule.notifyCategory !== ''
                          ? String(rule.notifyCategory)
                          : rule.notify_category != null && rule.notify_category !== ''
                            ? String(rule.notify_category)
                            : panelSceneId,
                      sceneLabel: panelSceneLabel,
                      defaultStoreId: userDefaultStore,
                      storeOptions,
                      chuanglanTemplateOptions,
                      onSuccess: this.handleRuleCreatedSuccess,
                      channelOptions: channelFilterOptions,
                      targetTypeOptions: this.props.notificationTargetTypeEnum.data || [],
                      offsetUnitOptions: this.props.notificationOffsetUnitEnum.data || [],
                      lifecycleOptions: this.props.notificationLifecycleEnum.data || []
                    }}
                    width={680}
                  >
                    <Button
                      type="link"
                      size="small"
                      className="notification-rule-card__edit"
                      icon={<EditOutlined />}
                    >
                      编辑
                    </Button>
                  </NotificationRuleModal>
                  <NotificationRuleCopyModal
                    baseProps={{
                      ruleId: rule.id,
                      ruleName: rule.name,
                      storeOptions: copyTargetStoreOptions(rule, storeOptions),
                      onSuccess: this.handleRuleCreatedSuccess
                    }}
                    width={480}
                  >
                    <Button
                      type="link"
                      size="small"
                      className="notification-rule-card__copy"
                      icon={<CopyOutlined />}
                      disabled={ruleCopyFetching}
                    >
                      拷贝到其他店铺
                    </Button>
                  </NotificationRuleCopyModal>
                  <Popconfirm
                    title="确定删除该通知规则？"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ loading: ruleDeleteFetching }}
                    onConfirm={() => this.handleDeleteNotificationRule(rule.id)}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      className="notification-rule-card__delete"
                      disabled={ruleDeleteFetching}
                      icon={<DeleteOutlined />}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                </div>
              </div>
              <div className="template-card-content">
                <div className="template-info-item">
                  <span className="label">门店：</span>
                  <span className="value">
                    {renderTextStr(ruleStoreDisplayName(rule, storeOptions))}
                  </span>
                </div>
                <div className="template-info-item">
                  <span className="label">触发：</span>
                  <span className="value">
                    {renderTextStr(
                      rule.triggerDescription ||
                        rule.triggerTypeDesc ||
                        rule.triggerType ||
                        '—'
                    )}
                  </span>
                </div>
                <div className="template-info-item">
                  <span className="label">触发间隔：</span>
                  <span className="value">
                    {renderTextStr(this.formatRuleTriggerInterval(rule))}
                  </span>
                </div>
                <div className="template-info-item">
                  <span className="label">目标：</span>
                  <span className="value">
                    {renderTextStr(
                      rule.targetTypeDesc ||
                        enumOptionLabel(targetTypeOptions, rule.targetType) ||
                        rule.targetType ||
                        '—'
                    )}
                  </span>
                </div>
                <div className="template-info-item">
                  <span className="label">模版内容：</span>
                  <span className="value content-text">
                    {renderTextStr(rule.messageTemplate || rule.message_template || '')}
                  </span>
                </div>
              </div>
              <div className="template-info-item meta">
                <span>更新：{formatDateTimeStr(rule.updatedAt)}</span>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>
    ))
  }

  render() {
    const currentQuery = this.getQuery()
    const { notificationRules } = this.props.serviceNotificationObj
    const {
      notificationSceneEnum: { data: templateTypeOptions, fetching: sceneEnumFetching }
    } = this.props
    const {
      notificationChannelEnum: { data: channelFilterOptions }
    } = this.props
    const {
      notificationTargetTypeEnum: { fetching: targetTypeEnumFetching },
      notificationOffsetUnitEnum: { fetching: offsetUnitEnumFetching },
      notificationLifecycleEnum: { fetching: lifecycleEnumFetching }
    } = this.props
    const { data: ruleData, fetching: ruleFetching } = notificationRules
    const { fetching: jStoreFetching } = this.props.jStoreListObj || {
      fetching: false
    }
    const { fetching: chuanglanTemplateFetching } = this.props.chuanglanTemplateOptionsObj || {
      fetching: false
    }

    const sceneCollapseKeys = templateTypeOptions.map((o) => o.id)

    // 启用状态选项
    const enabledOptions = [
      { id: 'true', value: '启用' },
      { id: 'false', value: '禁用' }
    ]

    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
        />
        <div className="i2-container">
          <Card className="i2-card-page">
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                <InputC
                  label="模板名称"
                  query={currentQuery}
                  searchKey="templateName"
                  action={this.updateQuery}
                  placeholder="请输入模板名称"
                />
                <SelectC
                  label="模板类型"
                  query={currentQuery}
                  searchKey="templateType"
                  options={templateTypeOptions}
                  action={this.updateQuery}
                  placeholder="模板类型"
                />
                <SelectC
                  label="通知渠道"
                  query={currentQuery}
                  searchKey="channel"
                  options={channelFilterOptions}
                  action={this.updateQuery}
                  placeholder="通知渠道"
                />
                <SelectC
                  label="启用状态"
                  query={currentQuery}
                  searchKey="enabled"
                  options={enabledOptions}
                  action={this.updateQuery}
                  placeholder="启用状态"
                />
              </div>
              {/* <div className="toolbar-actions">
                <ServiceNotificationModal
                  baseProps={{
                    editingTemplate: null,
                    onSuccess: this.handleSaveSuccess,
                    templateTypeOptions,
                    channelOptions: channelFilterOptions
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                  >
                    新建模板
                  </Button>
                </ServiceNotificationModal>
              </div> */}
            </section>
            <Spin
              spinning={
                sceneEnumFetching ||
                ruleFetching ||
                targetTypeEnumFetching ||
                offsetUnitEnumFetching ||
                lifecycleEnumFetching ||
                jStoreFetching ||
                chuanglanTemplateFetching
              }
            >
              {templateTypeOptions.length > 0 ? (
                <div className="notification-scene-baskets">
                  <Collapse
                    className="notification-scene-collapse"
                    defaultActiveKey={sceneCollapseKeys}
                    bordered={false}
                    expandIconPosition="end"
                  >
                    {templateTypeOptions.map((opt) => {
                      const sceneRules = ruleData.byNotifyCategory[opt.id] || []
                      console.log('ruleData', ruleData, 'opt', opt, 'sceneRules', sceneRules)
                      return (
                        <Panel
                          key={opt.id}
                          header={(
                            <div className="notification-scene-panel-header">
                              <div className="notification-scene-panel-header__left">
                                <span className="notification-scene-panel-header__ribbon" aria-hidden />
                                <div className="notification-scene-panel-header__titles">
                                  <Tag
                                    color={opt.color}
                                    className="notification-scene-panel-header__scene-tag"
                                  >
                                    {opt.value}
                                  </Tag>
                                  <span className="notification-scene-panel-header__tier">场景模版</span>
                                </div>
                              </div>
                              <span className="notification-scene-panel-header__stat">
                                {sceneRules.length} 条规则
                              </span>
                            </div>
                          )}
                        >
                          <div className="notification-scene-panel-actions">
                            <NotificationRuleModal
                              baseProps={{
                                actType: 'create',
                                defaultScene: opt.id,
                                sceneLabel: opt.value,
                                defaultStoreId: getLoginUserStoreId(this.props.user),
                                storeOptions: (this.props.jStoreListObj && this.props.jStoreListObj.data) || [],
                                chuanglanTemplateOptions:
                                  (this.props.chuanglanTemplateOptionsObj &&
                                    this.props.chuanglanTemplateOptionsObj.data) ||
                                  [],
                                onSuccess: this.handleRuleCreatedSuccess,
                                channelOptions: channelFilterOptions,
                                targetTypeOptions: this.props.notificationTargetTypeEnum.data,
                                offsetUnitOptions: this.props.notificationOffsetUnitEnum.data,
                                lifecycleOptions: this.props.notificationLifecycleEnum.data || []
                              }}
                            >
                              <Button type="primary" size="small" icon={<PlusOutlined />}>
                                新增规则
                              </Button>
                            </NotificationRuleModal>
                          </div>
                          {this.renderNotificationRulesInScenePanel(sceneRules, {
                            id: opt.id,
                            value: opt.value
                          })}
                        </Panel>
                      )
                    })}
                  </Collapse>
                </div>
              ) : (
                !sceneEnumFetching && (
                  <Empty
                    description="未获取到通知场景配置"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                  />
                )
              )}
            </Spin>
          </Card>
        </div>
      </div>
    )
  }
}

const ServiceNotificationListPageWrapper = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const user = useSelector((state) => state.app.user)
  
  const serviceNotificationState = useSelector(state => state.order?.serviceNotification || {})
  const notificationSceneEnum = useSelector(
    (state) => state.components.enumOptions.NotifyCategory
  )
  const notificationChannelEnum = useSelector(
    (state) => state.components.enumOptions.NotificationChannel
  )
  const notificationTargetTypeEnum = useSelector(
    (state) => state.components.enumOptions.NotificationTargetType
  )
  const notificationOffsetUnitEnum = useSelector(
    (state) => state.components.enumOptions.NotificationOffsetUnit
  )
  const notificationLifecycleEnum = useSelector(
    (state) => state.components.enumOptions.NotificationLifecycle
  )
  const jStoreListObj = useSelector((state) => state.components.getJStoreList)
  const chuanglanTemplateOptionsObj = useSelector(
    (state) => state.components.chuanglanTemplateOptions
  )

  const props = {
    dispatch,
    location,
    navigate,
    user,
    serviceNotificationObj: serviceNotificationState,
    notificationSceneEnum,
    notificationChannelEnum,
    notificationTargetTypeEnum,
    notificationOffsetUnitEnum,
    notificationLifecycleEnum,
    jStoreListObj,
    chuanglanTemplateOptionsObj
  }
  
  return <ServiceNotificationListPage {...props} />
}

export default ServiceNotificationListPageWrapper

